<script setup lang="ts">
import type { GA4Entities, GA4Events } from "nuxt-ga4"
import {
  MultipackOptions,
  ProductDetailsProps
} from "~/components/Pages/ProductDetails.props"
import { addToCartPosition } from "~/utils/constants"
import { productUtils } from "~/utils/product"
import type { AggregateRating, Review } from "schema-dts"
import { PRODUCT_PAGE, TApiErrorType } from "./mp-[product].const"
import { formatProduct } from "~/utils/analytics/analytics"
import consola from "consola"
import { Bundle, ListResponse } from "@commercelayer/sdk"
import { skipHydrate } from "pinia"

const { t } = useI18n()
const route = useRoute()
const { locale } = useI18n()
const { $cl } = useNuxtApp()
const { addToCart } = useCart()
const { sendEventAddToCart } = useGA4SiteEvents()

const requestId = route.path + "_" + Date.now()

const showAddToCartError = ref(false)
const notificationAlert = ref({ ...PRODUCT_PAGE.addToCart.defaultAlertProps })

const productSEO = computed(() => productData.value?.seo)

const storeLocationIdis = {
  localWarehouse:
    useRuntimeConfig().public.commerceLayer.LOCAL_WAREHOUSE_STOCK_LOCATION_ID,
  dropshipping:
    useRuntimeConfig().public.commerceLayer.DROPSHIPPING_STOCK_LOCATION_ID
}

onUnmounted(() => {
  clearNuxtState("skuDataState")
})

interface FeedatyProductReviews {
  reviewsCount: number
  reviewsAverage: number
}

import.meta.server &&
  consola.info(
    new Date().toISOString(),
    "Product page rendering start",
    route.path,
    requestId
  )

import.meta.server &&
  consola.info(
    new Date().toISOString(),
    "Product page Gluon start",
    route.path,
    requestId
  )

const { data: productData, error } = await usePage(
  `/api/gluon/fetchProductMultipackPage/${locale.value}/${
    route.params.product as string
  }/product.js`
)

import.meta.server &&
  consola.info(
    new Date().toISOString(),
    "Product page Gluon end",
    route.path,
    requestId
  )

// Get reviews
let reviews:
  | {
      aggregateRating?: AggregateRating
      review: Review[]
    }
  | undefined = undefined

if (import.meta.server) {
  const { data: cachedReviews, error } = await useFetch(
    `/api/reviews/${productData.value?.page?.details?.parentProductId}/summary`,
    {
      timeout: 1000,
      transform: (res: FeedatyProductReviews) => {
        let reviews: {
          aggregateRating?: AggregateRating
          review: Review[]
        } =
          (res?.reviewsAverage ?? 0) > 0 &&
          (res?.reviewsCount ?? 0) > 0
            ? {
                aggregateRating: {
                  "@type": "AggregateRating",
                  ratingValue: res.reviewsAverage,
                  reviewCount: res.reviewsCount
                },
                review: []
              }
            : {
                review: []
              }
        return reviews
      }
    }
  )
  if (error.value) {
    import.meta.server &&
      consola.error(
        new Date().toISOString(),
        "Product page error",
        "Error loading Feedaty reviews",
        route.path,
        requestId,
        error.value
      )
  }
  reviews = cachedReviews?.value ?? undefined
}

const alternativeProducts = route.hash === "#alternativeProducts"

type SkuData = {
  id: string
  price: number
  oldPrice: number
  omnibusPrice?: number
  currency: string
  stockQuantity: number
  localWarehouseStock: number
  dropshippingStock: number
  multipackOptions: MultipackOptions[]
}

const skuData = ref<SkuData | null>()
const expirationDate = ref<string | null>()

if (error.value) {
  import.meta.server &&
    consola.error(
      new Date().toISOString(),
      "Product page error",
      "Error loading Gluon data",
      route.path,
      requestId,
      error.value
    )
  throw createError({
    message: error.value.message,
    statusCode: error.value.statusCode ?? 404
  })
}

import.meta.server &&
  consola.info(
    new Date().toISOString(),
    "Product page Commercelayer start",
    route.path,
    requestId
  )

if (productData.value?.page?.details?.productId) {
  try {
    const multipackOptions =
      productData.value?.page?.multipackOptions
        ?.filter((option) => !option.current)
        .map((option) => option.productCode) ?? []
    const [[sku], bundles] = await Promise.all([
      $cl.skus.list({
        include: [
          "prices",
          "stock_items",
          "stock_items.reserved_stock",
          "stock_items.stock_location"
        ],
        fields: {
          prices: [
            "currency_code",
            "amount_cents",
            "amount_float",
            "compare_at_amount_cents",
            "compare_at_amount_float"
          ]
        },
        filters: { code_eq: productData.value?.page?.details?.parentProductId! }
      }),
      multipackOptions.length > 0
        ? $cl.bundles.list({
            include: [
              "sku_list.sku_list_items.sku.prices",
              "sku_list.sku_list_items.sku.stock_items",
              "sku_list.sku_list_items.sku.stock_items.reserved_stock",
              "sku_list.sku_list_items.sku.stock_items.stock_location"
            ],
            fields: {
              prices: [
                "currency_code",
                "amount_cents",
                "amount_float",
                "compare_at_amount_cents",
                "compare_at_amount_float"
              ]
            },
            filters: {
              code_in:
                productData.value?.page?.multipackOptions?.map(
                  (option) => option.productCode
                ) ?? []
            }
          })
        : new Promise<ListResponse<Bundle>>((resolve) =>
            resolve([] as unknown as ListResponse<Bundle>)
          )
    ])

    const bundle = bundles.find(
      (bundle) => bundle.code == productData.value?.page?.details?.productId
    )
    const parentSinglePackOption =
      productData.value?.page?.multipackOptions?.find(
        (option) => option.quantity == 1
      )

    if (
      !productUtils.isIndexableStatus(
        productData.value?.page?.sellabilityStatus
      ) &&
      !productUtils.calculateMultiStockLocationAvailability(sku) &&
      productData.value?.page?.sellabilityRedirectPath &&
      productData.value?.page?.sellabilityRedirectStatus
    ) {
      await navigateTo(productData.value.page.sellabilityRedirectPath, {
        redirectCode: productData.value.page.sellabilityRedirectStatus,
        external: true
      })
    } /*else if (
      parentSinglePackOption &&
      !productUtils.calculateBundleMultiStockLocationAvailability(bundle)
    ) {
      await navigateTo(parentSinglePackOption.slug, {
        redirectCode: 302,
        external: true
      })
    }*/ // https://lafarmacia.atlassian.net/browse/TF-3825 - disabled redirect to single pack option for now, as it is not a requirement anymore

    if (bundle?.sku_list?.sku_list_items?.length) {
      skuData.value = {
        id: bundle.id,
        price: bundle.price_amount_float ?? 0,
        oldPrice: productUtils.calculateBundleOriginalPrice(bundle),
        omnibusPrice: bundle.metadata?.omnibus
          ? parseFloat(bundle.metadata.omnibus)
          : undefined,
        currency: bundle.currency_code ?? "EUR",
        stockQuantity:
          productUtils.calculateBundleMultiStockLocationStock(bundle),
        localWarehouseStock: productUtils.calculateBundleStockByLocation(
          bundle,
          storeLocationIdis.localWarehouse
        ),
        dropshippingStock: productUtils.calculateBundleStockByLocation(
          bundle,
          storeLocationIdis.dropshipping
        ),
        multipackOptions:
          productData.value?.page?.multipackOptions
            ?.map((option) => {
              const bundleOption = bundles.find(
                (b) => b.code == option.productCode
              )
              const isSku = sku.code == option.productCode
              const bundleAvailable = !isSku
                ? productUtils.calculateBundleMultiStockLocationAvailability(
                    bundleOption
                  )
                : false
              return !isSku && !bundleAvailable
                ? null
                : {
                    current: option.current,
                    productCode: option.productCode,
                    productName: option.productName,
                    slug: option.slug,
                    price:
                      (isSku
                        ? sku.prices?.[0].amount_float
                        : bundleOption?.price_amount_float) ?? 0,
                    oldPrice:
                      (isSku
                        ? sku.prices?.[0].compare_at_amount_float
                        : productUtils.calculateBundleOriginalPrice(
                            bundleOption
                          )) ?? 0,
                    quantity: option.quantity
                  }
            })
            ?.filter((option) => (option?.price ?? 0) > 0) ?? []
      }

      if (import.meta.server) {
        useState<SkuData | null | undefined>(
          "skuDataState",
          () => skuData.value
        )
      }
      expirationDate.value =
        sku?.stock_items?.[0]?.metadata?.earliest_expiration_date
    }
  } catch (err) {
    import.meta.server &&
      consola.error(
        new Date().toISOString(),
        "Product page error",
        "Error loading Commercelayer information",
        route.path,
        requestId,
        err
      )
  }
}

import.meta.server &&
  consola.info(
    new Date().toISOString(),
    "Product page Commercelayer end",
    route.path,
    requestId
  )

const dataWithPriceAndQuantity = computed(():
  | ProductDetailsProps
  | undefined => {
  if (!productData.value) return

  const serverSideSkuData = useState<SkuData | null | undefined>("skuDataState")

  return {
    ...productData.value?.page,
    details: {
      ...productData.value?.page?.details,
      skuId: skuData.value?.id ?? serverSideSkuData?.value?.id!,
      price: skuData.value?.price ?? serverSideSkuData?.value?.price!,
      oldPrice: skuData.value?.oldPrice ?? serverSideSkuData?.value?.oldPrice,
      omnibusPrice:
        skuData.value?.omnibusPrice ?? serverSideSkuData?.value?.omnibusPrice,
      stockQuantity:
        skuData.value?.stockQuantity ?? serverSideSkuData?.value?.stockQuantity,
      localWarehouseStock:
        skuData.value?.localWarehouseStock ??
        serverSideSkuData?.value?.localWarehouseStock,
      dropshippingStock:
        skuData.value?.dropshippingStock ??
        serverSideSkuData?.value?.dropshippingStock,
      expirationDate: expirationDate.value ?? ""
    },
    multipackOptions:
      skuData.value?.multipackOptions ??
      serverSideSkuData?.value?.multipackOptions,
    description: {
      ...productData.value.page.description,
      infos: {
        ...productData.value.page.description.infos,
        dueDate: expirationDate.value ?? ""
      }
    },
    reviews,
    brand: productData?.value?.page?.brand
  }
})

const handleAddToCart = ({
  quantity,
  item
}: {
  quantity: number
  item: ProductDetailsProps
}) => {
  if (!skuData.value || !dataWithPriceAndQuantity.value) return
  const algoliaLastClickedProduct = localStorage?.getItem(
    "algolia-last-clicked-product"
  )
  const queryID =
    algoliaLastClickedProduct &&
    JSON.parse(algoliaLastClickedProduct).path === route.path
      ? JSON.parse(algoliaLastClickedProduct).queryID
      : ""

  addToCart(
    {
      bundle_code: item.details.productId,
      metadata: {
        product_image: item?.details.gallery[0]!,
        slug: `/mp-${item.details.slug}`,
        price: skuData.value.price,
        oldPrice: skuData.value.oldPrice,
        inStock: skuData.value.stockQuantity > 0,
        unitType: item.description?.infos?.unitOfMeasure ?? "",
        expirationDate: expirationDate.value ?? "",
        isDeductable: item.details.isDeductable ?? false,
        ga4Item: GAItem.value ?? {},
        afterSearch: {
          queryID
        }
      }
    },
    quantity
  )
    .then(() => {
      try {
        sendEventAddToCart(
          { ...GAItem.value, quantity },
          {
            currentPrice: GAItem.value.price * quantity,
            position: addToCartPosition.pdp
          }
        )
      } catch (error) {
        console.error("PDP GA add_to_cart tracking failed", error)
      }
    })
    .catch((apiError) => {
      apiError
      if (apiError.status === PRODUCT_PAGE.addToCart.apiErrorCheck.statusCode) {
        if (apiError.errors.length > 0) {
          const error = apiError.errors.find(
            (e) => e.code === PRODUCT_PAGE.addToCart.apiErrorCheck.code
          )
          setErrorMessage(error)
          showAddToCartError.value = true
        }
      }
      throw apiError
    })
}

const setErrorMessage = (error: any) => {
  let message = ""

  if (error && error.meta) {
    switch (error.meta.error) {
      case TApiErrorType.lessThanOrEqualTo:
        message = t(
          `productPage.notificationAlerts.addToCardError.${error.meta.error}.message`
        )
        break

      case TApiErrorType.outOfStock:
        message = t(
          `productPage.notificationAlerts.addToCardError.${error.meta.error}.message`
        )
        break

      default:
        message = t(
          "productPage.notificationAlerts.addToCardError.generic_error.message"
        )
        break
    }
  } else {
    message = t(
      "productPage.notificationAlerts.addToCardError.generic_error.message"
    )
  }

  notificationAlert.value = {
    ...notificationAlert.value,
    notificationAlertText: message
  }
}

const getRemainingProductString = (meta: any): string => {
  const key =
    "productPage.notificationAlerts.addToCardError." +
    meta.error +
    "." +
    (meta.count === 1 ? "singleRemaining" : "multipleRemaining")
  return t(key, { remainingProduct: meta.count })
}

const productCode = computed(() => {
  if (!dataWithPriceAndQuantity.value) return
  return dataWithPriceAndQuantity.value?.details?.productId ?? ""
})

/** get event event_select_item_{productCode} */
const { event: selectItemEvent } = useGAEvent(
  `event_select_item_${productCode.value ?? ""}`,
  "custom_event"
)

/** Define single GA4 item */
const GAItem = computed((): GA4Entities["gaItem"] => {
  let itemList = ""
  const dataWithPriceAndQuantityValue = dataWithPriceAndQuantity?.value
  const dataWithPriceAndQuantityBreadcrumbs =
    dataWithPriceAndQuantityValue?.breadcrumbs
  const dataWithPriceAndQuantityDetails = dataWithPriceAndQuantityValue?.details

  let itemListId = ""
  if (Object.keys(selectItemEvent.value).length) {
    itemList = selectItemEvent.value?.ecommerce?.items?.[0].item_list_name ?? ""
    itemListId = selectItemEvent.value?.ecommerce?.items?.[0].item_list_id ?? ""
  }

  return {
    item_id: dataWithPriceAndQuantityValue?.details?.productId ?? "",
    item_name: dataWithPriceAndQuantityValue?.details?.title ?? "",
    index: 0,
    price: skuData.value?.price
      ? parseFloat(skuData?.value?.price.toString())
      : "",
    item_list_name: itemList,
    item_list_id: itemListId,
    item_brand: dataWithPriceAndQuantityValue?.brand?.brandName ?? "",
    item_category: dataWithPriceAndQuantityBreadcrumbs?.links[0]?.label,
    item_category2: dataWithPriceAndQuantityBreadcrumbs?.links[1]?.label,
    item_category3: dataWithPriceAndQuantityBreadcrumbs?.links[2]?.label,
    item_category4: dataWithPriceAndQuantityBreadcrumbs?.links[3]?.label,
    item_category5: dataWithPriceAndQuantityBreadcrumbs?.links[4]?.label,
    item_category6: dataWithPriceAndQuantityBreadcrumbs?.links[5]?.label,
    currency: "EUR",
    discount:
      dataWithPriceAndQuantityDetails?.oldPrice &&
      dataWithPriceAndQuantityDetails?.price
        ? dataWithPriceAndQuantityDetails.oldPrice -
          dataWithPriceAndQuantityDetails.price
        : "",
    available:
      dataWithPriceAndQuantityDetails?.stockQuantity &&
      dataWithPriceAndQuantityDetails?.stockQuantity > 0
        ? "si"
        : "no",
    quantity: 1
  }
})

/** Define view_item payload event */
const viewItemEventPayload = computed((): GA4Events["custom_event"] => {
  return {
    event: "view_item",
    action: "view_item",
    ecommerce: {
      value: dataWithPriceAndQuantity.value?.details?.price
        ? dataWithPriceAndQuantity?.value?.details?.price
        : "",
      currency: "EUR",
      items: [formatProduct(GAItem.value)]
    }
  }
})

const closeAlert = () => {
  showAddToCartError.value = false
}
</script>

<template>
  <div>
    <SeoBase v-bind="productSEO" />
    <div class="fixed z-50 w-full p-4" v-if="showAddToCartError">
      <NotificationAlert v-bind="notificationAlert" @onClose="closeAlert()" />
    </div>
    <GAEvent
      :eventKey="(`event_view_item_${productCode ?? ''}` as string)"
      event="custom_event"
      :payload="viewItemEventPayload"
    >
      <PagesProductDetails
        v-if="dataWithPriceAndQuantity"
        v-bind="dataWithPriceAndQuantity"
        :scroll-to-alternative-products="alternativeProducts"
        @onPurchase="
          handleAddToCart({ quantity: $event, item: dataWithPriceAndQuantity })
        "
        :is-bundle="true"
      />
    </GAEvent>
  </div>
</template>
